import { TaxJurisdiction } from '@/types/taxExplorer';
import { COUNTRY_TAX_CURRENCY, toUsd } from '@/lib/fxToUsd';

// Priority jurisdictions with detailed tax rules
// Keyed by ISO Alpha-2 to match locations table
export const taxRulesOverrides: Record<string, TaxJurisdiction> = {
  'AE': {
    code: 'AE',
    personalIncomeTax: { type: 'none', topRate: 0 },
    capitalGainsTax: { rate: 0 },
    dividendTax: { rate: 0 },
    wealthTax: { hasWealthTax: false },
    inheritanceTax: { rate: 0 },
    residencyRule: { daysRequired: 183, testType: '183-day', notes: 'Golden Visa available' },
    lifestyle: { climate: 'warm', englishSpeaking: true, euAccess: false, cryptoFriendly: true, digitalNomadVisa: true, qualityOfLife: 8 }
  },
  'PT': {
    code: 'PT',
    personalIncomeTax: { 
      type: 'progressive', 
      topRate: 48,
      brackets: [
        { threshold: 7479, rate: 14.5 },
        { threshold: 11284, rate: 21 },
        { threshold: 15992, rate: 26.5 },
        { threshold: 20700, rate: 28.5 },
        { threshold: 26355, rate: 35 },
        { threshold: 38632, rate: 37 },
        { threshold: 50483, rate: 43.5 },
        { threshold: 78834, rate: 45 },
        { threshold: Infinity, rate: 48 }
      ],
      specialPrograms: ['NHR (Non-Habitual Resident)']
    },
    capitalGainsTax: { rate: 28, exemptions: ['Primary residence after 2 years'] },
    dividendTax: { rate: 28 },
    wealthTax: { hasWealthTax: false },
    inheritanceTax: { rate: 10 },
    residencyRule: { daysRequired: 183, testType: '183-day' },
    lifestyle: { climate: 'warm', englishSpeaking: true, euAccess: true, cryptoFriendly: true, digitalNomadVisa: true, qualityOfLife: 8 }
  },
  'ES': {
    code: 'ES',
    personalIncomeTax: {
      type: 'progressive',
      topRate: 47,
      brackets: [
        { threshold: 12450, rate: 19 },
        { threshold: 20200, rate: 24 },
        { threshold: 35200, rate: 30 },
        { threshold: 60000, rate: 37 },
        { threshold: 300000, rate: 45 },
        { threshold: Infinity, rate: 47 }
      ],
      specialPrograms: ['Beckham Law']
    },
    capitalGainsTax: { rate: 26 },
    dividendTax: { rate: 26 },
    wealthTax: { hasWealthTax: true, rate: 2.5, threshold: 700000 },
    inheritanceTax: { rate: 34 },
    residencyRule: { daysRequired: 183, testType: '183-day' },
    lifestyle: { climate: 'warm', englishSpeaking: false, euAccess: true, cryptoFriendly: false, digitalNomadVisa: true, qualityOfLife: 8 }
  },
  'GB': {
    code: 'GB',
    personalIncomeTax: {
      type: 'progressive',
      topRate: 45,
      brackets: [
        { threshold: 12570, rate: 0 },
        { threshold: 50270, rate: 20 },
        { threshold: 125140, rate: 40 },
        { threshold: Infinity, rate: 45 }
      ],
      specialPrograms: ['Non-Dom regime (ending 2025)']
    },
    capitalGainsTax: { rate: 24 },
    dividendTax: { rate: 39.35 },
    wealthTax: { hasWealthTax: false },
    inheritanceTax: { rate: 40 },
    residencyRule: { daysRequired: 183, testType: 'SRT', notes: 'Statutory Residence Test' },
    lifestyle: { climate: 'temperate', englishSpeaking: true, euAccess: false, cryptoFriendly: true, digitalNomadVisa: false, qualityOfLife: 8 }
  },
  'US': {
    code: 'US',
    personalIncomeTax: {
      type: 'progressive',
      topRate: 37,
      brackets: [
        { threshold: 11600, rate: 10 },
        { threshold: 47150, rate: 12 },
        { threshold: 100525, rate: 22 },
        { threshold: 191950, rate: 24 },
        { threshold: 243725, rate: 32 },
        { threshold: 609350, rate: 35 },
        { threshold: Infinity, rate: 37 }
      ]
    },
    capitalGainsTax: { rate: 20 },
    dividendTax: { rate: 20 },
    wealthTax: { hasWealthTax: false },
    inheritanceTax: { rate: 40 },
    residencyRule: { daysRequired: 183, testType: 'substantial-presence', notes: 'Citizen-based taxation' },
    lifestyle: { climate: 'temperate', englishSpeaking: true, euAccess: false, cryptoFriendly: true, digitalNomadVisa: false, qualityOfLife: 7 }
  },
  'TH': {
    code: 'TH',
    personalIncomeTax: {
      type: 'progressive',
      topRate: 35,
      brackets: [
        { threshold: 150000, rate: 0 },
        { threshold: 300000, rate: 5 },
        { threshold: 500000, rate: 10 },
        { threshold: 750000, rate: 15 },
        { threshold: 1000000, rate: 20 },
        { threshold: 2000000, rate: 25 },
        { threshold: 5000000, rate: 30 },
        { threshold: Infinity, rate: 35 }
      ]
    },
    capitalGainsTax: { rate: 0, exemptions: ['No CGT on securities'] },
    dividendTax: { rate: 10 },
    wealthTax: { hasWealthTax: false },
    inheritanceTax: { rate: 10 },
    residencyRule: { daysRequired: 180, testType: '180-day' },
    lifestyle: { climate: 'warm', englishSpeaking: false, euAccess: false, cryptoFriendly: true, digitalNomadVisa: true, qualityOfLife: 7 }
  },
  'SG': {
    code: 'SG',
    personalIncomeTax: {
      type: 'progressive',
      topRate: 24,
      brackets: [
        { threshold: 20000, rate: 0 },
        { threshold: 30000, rate: 2 },
        { threshold: 40000, rate: 3.5 },
        { threshold: 80000, rate: 7 },
        { threshold: 120000, rate: 11.5 },
        { threshold: 160000, rate: 15 },
        { threshold: 200000, rate: 18 },
        { threshold: 240000, rate: 19 },
        { threshold: 280000, rate: 19.5 },
        { threshold: 320000, rate: 20 },
        { threshold: 500000, rate: 22 },
        { threshold: 1000000, rate: 23 },
        { threshold: Infinity, rate: 24 }
      ]
    },
    capitalGainsTax: { rate: 0 },
    dividendTax: { rate: 0 },
    wealthTax: { hasWealthTax: false },
    inheritanceTax: { rate: 0 },
    residencyRule: { daysRequired: 183, testType: '183-day' },
    lifestyle: { climate: 'warm', englishSpeaking: true, euAccess: false, cryptoFriendly: true, digitalNomadVisa: false, qualityOfLife: 9 }
  },
  'HK': {
    code: 'HK',
    personalIncomeTax: {
      type: 'progressive',
      topRate: 17,
      brackets: [
        { threshold: 50000, rate: 2 },
        { threshold: 100000, rate: 6 },
        { threshold: 150000, rate: 10 },
        { threshold: 200000, rate: 14 },
        { threshold: Infinity, rate: 17 }
      ]
    },
    capitalGainsTax: { rate: 0 },
    dividendTax: { rate: 0 },
    wealthTax: { hasWealthTax: false },
    inheritanceTax: { rate: 0 },
    residencyRule: { daysRequired: 180, testType: '180-day' },
    lifestyle: { climate: 'warm', englishSpeaking: true, euAccess: false, cryptoFriendly: true, digitalNomadVisa: false, qualityOfLife: 8 }
  },
  'MY': {
    code: 'MY',
    personalIncomeTax: {
      type: 'progressive',
      topRate: 30,
      brackets: [
        { threshold: 5000, rate: 0 },
        { threshold: 20000, rate: 1 },
        { threshold: 35000, rate: 3 },
        { threshold: 50000, rate: 6 },
        { threshold: 70000, rate: 11 },
        { threshold: 100000, rate: 19 },
        { threshold: 400000, rate: 25 },
        { threshold: 600000, rate: 26 },
        { threshold: 2000000, rate: 28 },
        { threshold: Infinity, rate: 30 }
      ],
      specialPrograms: ['Malaysia My Second Home']
    },
    capitalGainsTax: { rate: 0, exemptions: ['No CGT on shares'] },
    dividendTax: { rate: 0 },
    wealthTax: { hasWealthTax: false },
    inheritanceTax: { rate: 0 },
    residencyRule: { daysRequired: 182, testType: '182-day' },
    lifestyle: { climate: 'warm', englishSpeaking: true, euAccess: false, cryptoFriendly: false, digitalNomadVisa: true, qualityOfLife: 7 }
  },
  'CY': {
    code: 'CY',
    personalIncomeTax: {
      type: 'progressive',
      topRate: 35,
      brackets: [
        { threshold: 19500, rate: 0 },
        { threshold: 28000, rate: 20 },
        { threshold: 36300, rate: 25 },
        { threshold: 60000, rate: 30 },
        { threshold: Infinity, rate: 35 }
      ],
      specialPrograms: ['Non-Dom regime']
    },
    capitalGainsTax: { rate: 0, exemptions: ['No CGT on securities'] },
    dividendTax: { rate: 0 },
    wealthTax: { hasWealthTax: false },
    inheritanceTax: { rate: 0 },
    residencyRule: { daysRequired: 60, testType: '60-day rule', notes: '60-day rule for non-doms' },
    lifestyle: { climate: 'warm', englishSpeaking: true, euAccess: true, cryptoFriendly: true, digitalNomadVisa: true, qualityOfLife: 7 }
  },
  'MT': {
    code: 'MT',
    personalIncomeTax: {
      type: 'progressive',
      topRate: 35,
      brackets: [
        { threshold: 9100, rate: 0 },
        { threshold: 14500, rate: 15 },
        { threshold: 19500, rate: 25 },
        { threshold: 60000, rate: 25 },
        { threshold: Infinity, rate: 35 }
      ],
      specialPrograms: ['Global Residence Programme', 'Residence Programme']
    },
    capitalGainsTax: { rate: 0, exemptions: ['No CGT on securities for non-doms'] },
    dividendTax: { rate: 15 },
    wealthTax: { hasWealthTax: false },
    inheritanceTax: { rate: 0 },
    residencyRule: { daysRequired: 183, testType: '183-day' },
    lifestyle: { climate: 'warm', englishSpeaking: true, euAccess: true, cryptoFriendly: true, digitalNomadVisa: true, qualityOfLife: 7 }
  },
  'AD': {
    code: 'AD',
    personalIncomeTax: {
      type: 'progressive',
      topRate: 10,
      brackets: [
        { threshold: 24000, rate: 0 },
        { threshold: 40000, rate: 5 },
        { threshold: Infinity, rate: 10 }
      ]
    },
    capitalGainsTax: { rate: 10 },
    dividendTax: { rate: 0 },
    wealthTax: { hasWealthTax: false },
    inheritanceTax: { rate: 0 },
    residencyRule: { daysRequired: 183, testType: '183-day' },
    lifestyle: { climate: 'temperate', englishSpeaking: false, euAccess: false, cryptoFriendly: true, digitalNomadVisa: false, qualityOfLife: 8 }
  },
  'MC': {
    code: 'MC',
    personalIncomeTax: { type: 'none', topRate: 0 },
    capitalGainsTax: { rate: 0 },
    dividendTax: { rate: 0 },
    wealthTax: { hasWealthTax: false },
    inheritanceTax: { rate: 0 },
    residencyRule: { daysRequired: 183, testType: '183-day', notes: 'High net worth required' },
    lifestyle: { climate: 'warm', englishSpeaking: true, euAccess: false, cryptoFriendly: true, digitalNomadVisa: false, qualityOfLife: 9 }
  },
  'EE': {
    code: 'EE',
    personalIncomeTax: { type: 'flat', topRate: 20 },
    capitalGainsTax: { rate: 20 },
    dividendTax: { rate: 0, exemptions: ['Retained earnings not taxed'] },
    wealthTax: { hasWealthTax: false },
    inheritanceTax: { rate: 0 },
    residencyRule: { daysRequired: 183, testType: '183-day' },
    lifestyle: { climate: 'cold', englishSpeaking: true, euAccess: true, cryptoFriendly: true, digitalNomadVisa: true, qualityOfLife: 7 }
  },
  'BR': {
    code: 'BR',
    personalIncomeTax: {
      type: 'progressive',
      topRate: 27.5,
      brackets: [
        { threshold: 24511, rate: 0 },
        { threshold: 33919, rate: 7.5 },
        { threshold: 45012, rate: 15 },
        { threshold: 55976, rate: 22.5 },
        { threshold: Infinity, rate: 27.5 }
      ]
    },
    capitalGainsTax: { rate: 22.5 },
    dividendTax: { rate: 0 },
    wealthTax: { hasWealthTax: false },
    inheritanceTax: { rate: 8 },
    residencyRule: { daysRequired: 183, testType: '183-day' },
    lifestyle: { climate: 'warm', englishSpeaking: false, euAccess: false, cryptoFriendly: true, digitalNomadVisa: true, qualityOfLife: 6 }
  }
};

// Get tax rules for a country, with fallback to basic rate
// Thresholds are converted from the country's own currency into US dollars.
const usdCache: Record<string, TaxJurisdiction> = {};

function toUsdRules(rules: TaxJurisdiction): TaxJurisdiction {
  const currency = COUNTRY_TAX_CURRENCY[rules.code] ?? 'USD';
  return {
    ...rules,
    currency: 'USD',
    personalIncomeTax: {
      ...rules.personalIncomeTax,
      brackets: rules.personalIncomeTax.brackets?.map((b) => ({ ...b, threshold: toUsd(b.threshold, currency) })),
    },
    wealthTax: {
      ...rules.wealthTax,
      threshold: rules.wealthTax.threshold === undefined ? undefined : toUsd(rules.wealthTax.threshold, currency),
    },
  };
}

export function getTaxRulesForCountry(isoCode: string | null, fallbackRate: number = 25): TaxJurisdiction {
  if (isoCode && taxRulesOverrides[isoCode]) {
    return (usdCache[isoCode] ??= toUsdRules(taxRulesOverrides[isoCode]));
  }
  
  // Default structure for countries without detailed rules
  return {
    code: isoCode || 'XX',
    personalIncomeTax: { type: 'progressive', topRate: fallbackRate },
    capitalGainsTax: { rate: fallbackRate * 0.8 },
    dividendTax: { rate: fallbackRate * 0.6 },
    wealthTax: { hasWealthTax: false },
    inheritanceTax: { rate: 0 },
    residencyRule: { daysRequired: 183, testType: '183-day' },
    lifestyle: { climate: 'temperate', englishSpeaking: false, euAccess: false, cryptoFriendly: false, digitalNomadVisa: false, qualityOfLife: 5 }
  };
}
